import { useEffect, useRef, useState } from "react";
import { CoverEditor } from "./CoverEditor";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";
import { fetchBatchImagesByBookId } from "../../../../services/mediaService";
import { setCoverImages } from "../../../../redux/slice/sketchSlice";
import { useDispatch } from "react-redux";
import { setCoverImageUploadStatus } from "../../../../redux/slice/stepSlice";
import { setTitle } from "../../../../helpers/MetaTag";
import { loadPendingCoverImage } from "../../../../util/pendingImageStorage";

const CoverEditorScreen = () => {
  const dispatch = useDispatch();
  const coverImages = useSelector((state) => state.sketch.coverImages);
  const uploadingCoverImage = useSelector((state) => state.stepFlow.uploadingCoverImage);
  const forceBookRefresh = useSelector((state) => state.stepFlow.forceBookRefresh);
  
  // Track font loading
  const [fontLoaded, setFontLoaded] = useState(false);
  
  // Track the previous cover image URL
  const prevCoverImageRef = useRef(null);
  const coverImagesRef = useRef(coverImages);

  useEffect(() => {
    coverImagesRef.current = coverImages;
  }, [coverImages]);

  /** Same source as Review / sockets: book-scoped media. Profile `lastBatchId` is not filtered in the API and is often stale. */
  const activeBookId = useSelector((state) => state.stepFlow.activeBookId);
  const { data: batchImagesData, isLoading: isLoadingBatchImages, isFetching: isFetchingBatchImages } = useFetch({
    dependencies: ["coverDesignMedia", activeBookId, forceBookRefresh],
    enabled: !!activeBookId,
    fetchFunction: async () => {
      if (!activeBookId) return null;
      const result = await fetchBatchImagesByBookId(activeBookId);
      return result?.docs?.data || [];
    },
  });

  // useFetch hands back its copy from the last visit first. After a cover is
  // replaced that copy still holds the old drawing, which then showed as "ready"
  // until the new one was drawn. Only rows from this visit's own fetch may set
  // the cover: note when that fetch starts, and trust the rows once it ends.
  const fetchKey = `${activeBookId}:${forceBookRefresh}`;
  const fetchStartedFor = useRef(null);
  const [freshRowsFor, setFreshRowsFor] = useState(null);
  useEffect(() => {
    if (isFetchingBatchImages) fetchStartedFor.current = fetchKey;
    else if (fetchStartedFor.current === fetchKey) setFreshRowsFor(fetchKey);
  }, [isFetchingBatchImages, fetchKey]);
  const batchRowsAreFresh = freshRowsFor === fetchKey;

  setTitle("Memory Doodle | Design Cover");

  // Load and check font with auto-reload after 3 seconds
  useEffect(() => {
    let reloadTimeout;
    
    const checkFont = async () => {
      try {
        // Set a 2-second timeout to reload the page if font doesn't load
        reloadTimeout = setTimeout(() => {
          if (!fontLoaded) {
            console.log("Font loading timeout - reloading page...");
            window.location.reload();
          }
        }, 2000);

        // Load the font using the Font Loading API
        await document.fonts.load('1em "Permanent Marker"');
        
        // Check if font is ready
        const fontReady = document.fonts.check('1em "Permanent Marker"');
        
        if (fontReady) {
          setFontLoaded(true);
          clearTimeout(reloadTimeout);
        } else {
          // Fallback: wait for fonts to be ready
          await document.fonts.ready;
          setFontLoaded(true);
          clearTimeout(reloadTimeout);
        }
      } catch (error) {
        console.error("Font loading error:", error);
        // Set to true anyway to prevent infinite loading
        setFontLoaded(true);
        clearTimeout(reloadTimeout);
      }
    };

    checkFont();

    // Cleanup timeout on unmount
    return () => {
      if (reloadTimeout) {
        clearTimeout(reloadTimeout);
      }
    };
  }, [fontLoaded]);

  // Hydrate Redux from DB when we have media rows with a cover; never replace with [] (would wipe socket/pending state).
  // Skip while a new cover job is running so we do not flash the previous sketch.
  useEffect(() => {
    if (!batchRowsAreFresh) return;
    if (!batchImagesData || !Array.isArray(batchImagesData) || !batchImagesData.length) return;
    if (uploadingCoverImage) return;

    const pendingCover = activeBookId ? loadPendingCoverImage(activeBookId) : null;
    if (pendingCover?.sentToRun) return;

    const coverRow = batchImagesData.find((img) => img.isCover);
    if (!coverRow) return;

    const existing = coverImagesRef.current?.[0];
    const incomingAi =
      coverRow.AIUrl && String(coverRow.AIUrl).trim()
        ? String(coverRow.AIUrl).trim()
        : '';
    const existingAi =
      existing?.AIUrl && String(existing.AIUrl).trim()
        ? String(existing.AIUrl).trim()
        : '';

    // Do not let a stale pre-illustration fetch overwrite a finished cover from sockets.
    if (existingAi && !incomingAi) return;
    if (existingAi && incomingAi && existingAi === incomingAi) return;

    dispatch(
      setCoverImages([
        {
          ...coverRow,
          resultUrl: coverRow.AIUrl,
          originalUrl: coverRow.url,
          status: incomingAi ? "COMPLETED" : existing?.status || "PROCESSING",
          progress: incomingAi ? 100 : existing?.progress ?? 0,
        },
      ])
    );
  }, [batchImagesData, batchRowsAreFresh, dispatch, uploadingCoverImage, activeBookId]);

  // Detect when a NEW cover image arrives
  useEffect(() => {
    const currentCoverImage = coverImages.find(img => img.isCover);
    const currentCoverUrl = currentCoverImage?.AIUrl || currentCoverImage?.url || null;
    
    // If we're uploading and a new cover image URL appears (different from previous)
    if (uploadingCoverImage && currentCoverUrl && currentCoverUrl !== prevCoverImageRef.current) {
      dispatch(setCoverImageUploadStatus(false));
    }
    
    // Update the ref with current URL
    prevCoverImageRef.current = currentCoverUrl;
  }, [coverImages, uploadingCoverImage, dispatch]);

  // Show loading while font is loading
  if (!fontLoaded) {
    return <></>;
  }

  // Guard navigation only if there's truly no data
  if (!coverImages || coverImages.length === 0) {
    if (isLoadingBatchImages) return <div>Loading...</div>;
    return null;
  }

  const coverImage = coverImages[0];

  return <CoverEditor image={coverImage} />;
};

export default CoverEditorScreen;